/**
 * masters.operations.ts — the MASTERS document registry (B2.6'-fe-seam;
 * fullsuite plan §B2', extending the B1.3' seam pattern).
 *
 * The document TEXTS are single-sourced from the B0.7' contract pack
 * (`packages/contracts/operations/*.graphql` — one operation per nav page,
 * the persisted-operation safelist seed) via vite `?raw` imports, so the
 * facades can never drift from the reviewed contract text.
 *
 * INTERIM (same posture as graphql.operations.ts at B1.3'; removal trigger:
 * masters adoption in codegen.ts at the CU-1 flip): the hand-declared TYPES
 * below do NOT yet swap to generated `TypedDocumentNode` re-exports — at
 * this slice's authoring the masters SDL is NOT in the frozen snapshot
 * (the B2' graph slices land it), so `codegen.ts adoptedOperations` cannot
 * validate these documents yet. Until adoption they ride plain full-text
 * POSTs (graphql.client.ts null-hash rule). The projections mirror the
 * operation fragments field-for-field; drift between them and the contract
 * text is guarded by the mastersSeam vitest matrix (parse + basename +
 * `$first` contract).
 */

import vendorListPageText from '../../../packages/contracts/operations/VendorListPage.graphql?raw';
import vendorDetailPageText from '../../../packages/contracts/operations/VendorDetailPage.graphql?raw';
import customerListPageText from '../../../packages/contracts/operations/CustomerListPage.graphql?raw';
import customerDetailPageText from '../../../packages/contracts/operations/CustomerDetailPage.graphql?raw';
import contactListPageText from '../../../packages/contracts/operations/ContactListPage.graphql?raw';
import categoryListPageText from '../../../packages/contracts/operations/CategoryListPage.graphql?raw';
import categoryDetailPageText from '../../../packages/contracts/operations/CategoryDetailPage.graphql?raw';
// CU-1a masters flip — write/dedupe/collection/reference documents.
import companyByTaxIdCheckText from '../../../packages/contracts/operations/CompanyByTaxIdCheck.graphql?raw';
import vendorCreateText from '../../../packages/contracts/operations/VendorCreate.graphql?raw';
import vendorContactAddText from '../../../packages/contracts/operations/VendorContactAdd.graphql?raw';
import vendorContactsCollectionText from '../../../packages/contracts/operations/VendorContactsCollection.graphql?raw';
import vendorBankAccountAddText from '../../../packages/contracts/operations/VendorBankAccountAdd.graphql?raw';
import vendorBankAccountSetDefaultText from '../../../packages/contracts/operations/VendorBankAccountSetDefault.graphql?raw';
import vendorBanksCollectionText from '../../../packages/contracts/operations/VendorBanksCollection.graphql?raw';
import partyCategoryCreateText from '../../../packages/contracts/operations/PartyCategoryCreate.graphql?raw';
import partyCategorySetActiveText from '../../../packages/contracts/operations/PartyCategorySetActive.graphql?raw';
import currencyOptionsText from '../../../packages/contracts/operations/CurrencyOptions.graphql?raw';
// CU-1b masters ACTIVATION — the *SetActive roots, one per lifecycle toggle.
// Two roots the SDL declares are absent here ON PURPOSE:
// `customerBankAccountSetActive` (customers have no bank-account facade at
// all) and `masterDataContactSetActive` (the standalone contact toggle has no
// caller — masterDataContactApi's two toggles are party-scoped and ride the
// vendor/customer contact roots below). Neither has a call site, and a
// document with no call site is an unused safelist entry, not a flip.
import vendorSetActiveText from '../../../packages/contracts/operations/VendorSetActive.graphql?raw';
import vendorContactSetActiveText from '../../../packages/contracts/operations/VendorContactSetActive.graphql?raw';
import vendorLocationSetActiveText from '../../../packages/contracts/operations/VendorLocationSetActive.graphql?raw';
import vendorBankAccountSetActiveText from '../../../packages/contracts/operations/VendorBankAccountSetActive.graphql?raw';
import customerSetActiveText from '../../../packages/contracts/operations/CustomerSetActive.graphql?raw';
import customerContactSetActiveText from '../../../packages/contracts/operations/CustomerContactSetActive.graphql?raw';
import customerLocationSetActiveText from '../../../packages/contracts/operations/CustomerLocationSetActive.graphql?raw';

/** Operation names — must equal each file's basename (safelist identity). */
export const MASTERS_OPERATION_NAMES = {
  vendorList: 'VendorListPage',
  vendorDetail: 'VendorDetailPage',
  customerList: 'CustomerListPage',
  customerDetail: 'CustomerDetailPage',
  contactList: 'ContactListPage',
  categoryList: 'CategoryListPage',
  categoryDetail: 'CategoryDetailPage',
  companyByTaxIdCheck: 'CompanyByTaxIdCheck',
  vendorCreate: 'VendorCreate',
  vendorContactAdd: 'VendorContactAdd',
  vendorContactsCollection: 'VendorContactsCollection',
  vendorBankAccountAdd: 'VendorBankAccountAdd',
  vendorBankAccountSetDefault: 'VendorBankAccountSetDefault',
  vendorBanksCollection: 'VendorBanksCollection',
  partyCategoryCreate: 'PartyCategoryCreate',
  partyCategorySetActive: 'PartyCategorySetActive',
  currencyOptions: 'CurrencyOptions',
  vendorSetActive: 'VendorSetActive',
  vendorContactSetActive: 'VendorContactSetActive',
  vendorLocationSetActive: 'VendorLocationSetActive',
  vendorBankAccountSetActive: 'VendorBankAccountSetActive',
  customerSetActive: 'CustomerSetActive',
  customerContactSetActive: 'CustomerContactSetActive',
  customerLocationSetActive: 'CustomerLocationSetActive',
} as const;

export const mastersOperations = {
  vendorList: vendorListPageText,
  vendorDetail: vendorDetailPageText,
  customerList: customerListPageText,
  customerDetail: customerDetailPageText,
  contactList: contactListPageText,
  categoryList: categoryListPageText,
  categoryDetail: categoryDetailPageText,
  companyByTaxIdCheck: companyByTaxIdCheckText,
  vendorCreate: vendorCreateText,
  vendorContactAdd: vendorContactAddText,
  vendorContactsCollection: vendorContactsCollectionText,
  vendorBankAccountAdd: vendorBankAccountAddText,
  vendorBankAccountSetDefault: vendorBankAccountSetDefaultText,
  vendorBanksCollection: vendorBanksCollectionText,
  partyCategoryCreate: partyCategoryCreateText,
  partyCategorySetActive: partyCategorySetActiveText,
  currencyOptions: currencyOptionsText,
  vendorSetActive: vendorSetActiveText,
  vendorContactSetActive: vendorContactSetActiveText,
  vendorLocationSetActive: vendorLocationSetActiveText,
  vendorBankAccountSetActive: vendorBankAccountSetActiveText,
  customerSetActive: customerSetActiveText,
  customerContactSetActive: customerContactSetActiveText,
  customerLocationSetActive: customerLocationSetActiveText,
} as const;

// ---------------------------------------------------------------------------
// Shared graph shapes (exactly what the operations select — never a full
// schema model).
// ---------------------------------------------------------------------------

/** Relay connection slice as the page operations select it. */
export interface GqlConnection<TNode> {
  totalCount: number;
  pageInfo: {
    hasNextPage: boolean;
    endCursor: string | null;
  };
  edges: Array<{ node: TNode }>;
}

/** Party-category tag as embedded on vendor/customer rows. */
export interface GqlCategoryTag {
  id: string;
  /** Raw legacy row id (CU-1a id-canon — the relay `id` stays opaque). */
  categoryId: number;
  nameEn: string | null;
  nameZh: string | null;
}

export interface GqlPersonRef {
  id: string;
  /** Raw legacy numeric user id (CU-1a id-canon; null when non-numeric). */
  userId: number | null;
  fullName: string;
}

export interface GqlCompanyRef {
  id: string;
  companyName: string;
}

export interface GqlRegisteredAddress {
  address: string | null;
  postcode: string | null;
  stateId: number | null;
  cityId: number | null;
}

export interface GqlPaymentTermRef {
  id: string;
  nameEn: string | null;
  nameZh: string | null;
  hasDiscount: boolean;
}

// ---------------------------------------------------------------------------
// Vendor
// ---------------------------------------------------------------------------

/** Full embedded category tag (vendor LIST rows — the mock list fixture
 * embeds the whole category object; CU-1a read-leg parity). */
export interface GqlFullCategoryTag extends GqlCategoryTag {
  description: string | null;
  color: string | null;
  isActive: boolean;
  typeInfo: Array<{ typeId: number; type: string }>;
}

/** VendorListRow fragment projection (the LEGACY 9-key list row). */
export interface GqlVendorListNode {
  id: string;
  /** Raw legacy row id (CU-1a id-canon). */
  vendorId: number;
  companyName: string;
  localTaxId: string | null;
  /** Entered-at-the-time list display cells (0014). */
  listAddress: string | null;
  listPhone: string | null;
  individual: boolean;
  isActive: boolean;
  categories: GqlFullCategoryTag[];
}

export interface VendorListPageResult {
  vendors: GqlConnection<GqlVendorListNode>;
  vendorCategoryOptions: GqlConnection<GqlCategoryTag>;
}

/** VendorDetailFields fragment projection (core scalar subset the facade maps). */
export interface GqlVendorDetailNode {
  id: string;
  /** Raw legacy row id (CU-1a id-canon). */
  vendorId: number;
  version: number;
  companyName: string;
  alternativeName: string | null;
  nickname: string | null;
  vendorCode: string | null;
  localTaxId: string | null;
  individual: boolean;
  countryId: number | null;
  email: string | null;
  web: string | null;
  fax: string | null;
  mobile: string | null;
  notes: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  phoneExtension: string | null;
  addressLang: string | null;
  internalContact: GqlPersonRef | null;
  registeredAddress: GqlRegisteredAddress | null;
  primaryPort: string | null;
  salesRepresentatives: GqlPersonRef[];
  parentCompany: GqlCompanyRef | null;
  categories: Array<GqlCategoryTag & { color: string | null }>;
  paymentTerm: GqlPaymentTermRef | null;
  isActive: boolean;
}

export interface VendorDetailPageResult {
  vendor: GqlVendorDetailNode | null;
}

// ---------------------------------------------------------------------------
// Customer
// ---------------------------------------------------------------------------

/** CustomerListRow fragment projection (the LEGACY list row). */
export interface GqlCustomerListNode {
  id: string;
  /** Raw legacy row id (CU-1a id-canon). */
  customerId: number;
  companyName: string;
  customerCode: string | null;
  localTaxId: string | null;
  /** Entered-at-the-time list display cells (0014). */
  listAddress: string | null;
  listPhone: string | null;
  individual: boolean;
  isActive: boolean;
  internalContact: GqlPersonRef | null;
  categories: GqlCategoryTag[];
}

export interface CustomerListPageResult {
  customers: GqlConnection<GqlCustomerListNode>;
  customerCategoryOptions: GqlConnection<GqlCategoryTag>;
}

/** CustomerDetailFields fragment projection (core scalar subset the facade maps). */
export interface GqlCustomerDetailNode {
  id: string;
  /** Raw legacy row id (CU-1a id-canon). */
  customerId: number;
  version: number;
  companyName: string;
  alternativeName: string | null;
  localTaxId: string | null;
  countryId: number | null;
  email: string | null;
  web: string | null;
  fax: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  phoneExtension: string | null;
  addressLang: string | null;
  registeredAddress: GqlRegisteredAddress | null;
  internalContact: GqlPersonRef | null;
  parentCompany: GqlCompanyRef | null;
  subsidiaries: GqlCompanyRef[];
  relatedOrganisations: GqlCompanyRef[];
  categories: GqlCategoryTag[];
  accountNotes: string | null;
  deliveryNotes: string | null;
  salesNotes: string | null;
  isActive: boolean;
}

export interface CustomerDetailPageResult {
  customer: GqlCustomerDetailNode | null;
}

// ---------------------------------------------------------------------------
// Master-data contact
// ---------------------------------------------------------------------------

/**
 * ContactListRow fragment projection. `linkedParties` is a Vendor|Customer
 * union whose branches both select {id, companyName}; the operation selects
 * NO `__typename`, so the side (vendor vs customer) is NOT distinguishable
 * from the wire — recorded as a masters-freeze register candidate (the
 * legacy list item carries `vendorId`/`customerId`).
 */
export interface GqlContactListNode {
  id: string;
  name: string;
  email: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  phoneExtension: string | null;
  isActive: boolean;
  linkedParties: GqlCompanyRef[];
  updatedAt: string | null;
}

export interface ContactListPageResult {
  masterDataContacts: GqlConnection<GqlContactListNode>;
}

// ---------------------------------------------------------------------------
// Party category (flat — the two-domain rule: NOT the 5-level taxonomy)
// ---------------------------------------------------------------------------

export interface GqlCategoryTypeInfo {
  typeId: number;
  type: string;
}

/** PartyCategoryListRow fragment projection. */
export interface GqlPartyCategoryListNode {
  id: string;
  /** Raw legacy row id (CU-1a id-canon). */
  categoryId: number;
  nameEn: string | null;
  nameZh: string | null;
  description: string | null;
  color: string | null;
  typeInfo: GqlCategoryTypeInfo[];
  memberCount: number;
  isActive: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CategoryListPageResult {
  partyCategories: GqlConnection<GqlPartyCategoryListNode>;
}

/** CategoryDetailPage projection (scalar subset the facade maps). */
export interface GqlPartyCategoryDetailNode {
  id: string;
  /** Raw legacy row id (CU-1a id-canon). */
  categoryId: number;
  version: number;
  nameEn: string | null;
  nameZh: string | null;
  description: string | null;
  color: string | null;
  typeInfo: GqlCategoryTypeInfo[];
  isActive: boolean;
}

export interface CategoryDetailPageResult {
  partyCategory: GqlPartyCategoryDetailNode | null;
}

// ---------------------------------------------------------------------------
// CU-1a masters flip — write/dedupe/collection/reference projections
// ---------------------------------------------------------------------------

/** UserError selection every mutation document pins. */
export interface GqlUserError {
  code: string;
  field: string | null;
  message: string;
}

/** CompanyByTaxIdCheck — the one-query dedupe shape (both wizard sides). */
export interface GqlCompanyPrefill {
  name: string;
  alternativeName: string | null;
  nickname: string | null;
  taxId: string | null;
  individual: boolean;
  email: string | null;
  fax: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  phoneExtension: string | null;
  mobile: string | null;
  web: string | null;
  countryId: number | null;
  stateId: number | null;
  cityId: number | null;
  postcode: string | null;
  address: string | null;
  addressLang: string | null;
  notes: string | null;
  primaryPort: string | null;
  categoryIds: number[];
}

export interface CompanyByTaxIdCheckResult {
  companyByTaxId: {
    vendor: GqlCompanyRef | null;
    customer: GqlCompanyRef | null;
    company: GqlCompanyPrefill | null;
    message: string;
  };
}

/** VendorCreatedFields projection (the created-vendor echo). */
export interface GqlVendorCreatedNode {
  id: string;
  /** Raw legacy row id (CU-1a id-canon). */
  vendorId: number;
  version: number;
  companyName: string;
  alternativeName: string | null;
  nickname: string | null;
  vendorCode: string | null;
  localTaxId: string | null;
  individual: boolean;
  countryId: number | null;
  email: string | null;
  web: string | null;
  fax: string | null;
  mobile: string | null;
  notes: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  phoneExtension: string | null;
  addressLang: string | null;
  registeredAddress: GqlRegisteredAddress | null;
  primaryPort: string | null;
  salesRepresentatives: GqlPersonRef[];
  internalContact: GqlPersonRef | null;
  categories: Array<GqlCategoryTag & { color: string | null }>;
  isActive: boolean;
}

export interface VendorCreateResult {
  vendorCreate: {
    vendor: GqlVendorCreatedNode | null;
    userErrors: GqlUserError[];
  };
}

/** PartyContact projection (legacy ContactResponse shape; FE numeric id). */
export interface GqlPartyContactNode {
  contactId: number;
  name: string;
  jobTitle: string | null;
  isPrimary: boolean;
  email: string | null;
  fax: string | null;
  phoneCountryCode: string | null;
  phoneNumber: string | null;
  phoneExtension: string | null;
  mobileCountryCode: string | null;
  mobileNumber: string | null;
  countryId: number | null;
  stateId: number | null;
  cityId: number | null;
  postcode: string | null;
  address: string | null;
  addressLang: string | null;
  isActive: boolean;
}

export interface VendorContactAddResult {
  vendorContactAdd: {
    contact: GqlPartyContactNode | null;
    userErrors: GqlUserError[];
  };
}

export interface VendorContactsCollectionResult {
  vendor: { id: string; contacts: GqlPartyContactNode[] } | null;
}

/** PartyBankAccount projection (legacy BankAccountResponse shape). */
export interface GqlPartyBankAccountNode {
  bankAccountId: number;
  /** Legacy corpus row key (seeded rows) — the wire bankId is bankId ?? bankAccountId. */
  bankId: number | null;
  bankCode: string | null;
  branchCode: string | null;
  accountName: string;
  accountNumber: string;
  currency: string | null;
  bankName: string | null;
  branchName: string | null;
  countryId: number | null;
  countryNameEn: string | null;
  countryNameZh: string | null;
  swiftCode: string | null;
  iban: string | null;
  notes: string | null;
  isDefault: boolean;
  isActive: boolean;
}

export interface VendorBankAccountAddResult {
  vendorBankAccountAdd: {
    bankAccount: GqlPartyBankAccountNode | null;
    userErrors: GqlUserError[];
  };
}

export interface VendorBankAccountSetDefaultResult {
  vendorBankAccountSetDefault: {
    bankAccount: GqlPartyBankAccountNode | null;
    userErrors: GqlUserError[];
  };
}

export interface VendorBanksCollectionResult {
  vendor: { id: string; bankAccounts: GqlPartyBankAccountNode[] } | null;
}

/** PartyCategory write echo (transformCategory shape). */
export interface GqlPartyCategoryWriteEchoNode {
  id: string;
  /** Raw legacy row id (CU-1a id-canon). */
  categoryId: number;
  nameEn: string | null;
  nameZh: string | null;
  description: string | null;
  color: string | null;
  typeInfo: GqlCategoryTypeInfo[];
  isActive: boolean;
}

export interface PartyCategoryCreateResult {
  partyCategoryCreate: {
    partyCategory: GqlPartyCategoryWriteEchoNode | null;
    userErrors: GqlUserError[];
  };
}

export interface PartyCategorySetActiveResult {
  partyCategorySetActive: {
    partyCategory: GqlPartyCategoryWriteEchoNode | null;
    userErrors: GqlUserError[];
  };
}

// ---------------------------------------------------------------------------
// CU-1b masters ACTIVATION — the *SetActive projections
//
// EVERY ONE OF THESE CARRIES `isActive` FROM THE CALLER, and that is the
// whole point of the slice.
//
// WHY NOT THE UPDATE ROOT — and the correction that came out of measuring it
// instead of assuming it (`mastersActivationWrite.test.ts` pins both halves):
//
//   PARENTS     `VendorUpdateInput`, `CustomerUpdateInput` and
//               `PartyCategoryUpdateInput` declare NO `isActive`. An
//               activation routed through them sends a patch the resolver
//               drops, answers `userErrors: []`, and reports success over an
//               unmoved row — the D-3 shape, which products paid for first.
//   SATELLITES  every {Vendor,Customer}{Contact,Location,BankAccount}
//               UpdateInput DOES declare `isActive`. This slice was authored
//               believing otherwise and the guard turned red on the first
//               run. They still ride the setter: `update` carries the whole
//               row, so putting a lifecycle toggle through it makes the
//               toggle depend on every other field being right and turns it
//               into a full-row overwrite. Single-purpose root, single field.
// ---------------------------------------------------------------------------

/** vendorSetActive echo — the same scalar subset `mapVendorDetailNode` reads. */
export interface VendorSetActiveResult {
  vendorSetActive: {
    vendor: GqlVendorDetailNode | null;
    userErrors: GqlUserError[];
  };
}

/**
 * customerSetActive echo — LEAN by design. The mock answers
 * {message, customerId, isActive} and `ToggleCustomerStatusResponse` agrees,
 * so the document selects no detail fields the wire never carried.
 */
export interface GqlCustomerSetActiveNode {
  id: string;
  /** Raw legacy row id (CU-1a id-canon). */
  customerId: number;
  version: number;
  isActive: boolean;
}

export interface CustomerSetActiveResult {
  customerSetActive: {
    customer: GqlCustomerSetActiveNode | null;
    userErrors: GqlUserError[];
  };
}

/** vendorContactSetActive — serves contactApi AND masterDataContactApi. */
export interface VendorContactSetActiveResult {
  vendorContactSetActive: {
    contact: GqlPartyContactNode | null;
    userErrors: GqlUserError[];
  };
}

/** customerContactSetActive — serves customerApi AND masterDataContactApi. */
export interface CustomerContactSetActiveResult {
  customerContactSetActive: {
    contact: GqlPartyContactNode | null;
    userErrors: GqlUserError[];
  };
}

/**
 * Location lifecycle echo — LEAN, and shared by both party sides.
 *
 * The two legacy toggle responses are structurally identical
 * ({message, locationId, isActive} via `ToggleStatusResponse`), and a
 * lifecycle toggle moves neither the vendor's `isDefaultReceiving` nor the
 * customer's `isDefaultShipping`/`isDefaultBilling` — so no default flag is
 * selected, and there is nothing left for the two sides to disagree about.
 */
export interface GqlLocationSetActiveNode {
  id: string;
  /** Raw legacy row id (CU-1a id-canon). */
  locationId: number;
  isActive: boolean;
}

export interface VendorLocationSetActiveResult {
  vendorLocationSetActive: {
    location: GqlLocationSetActiveNode | null;
    userErrors: GqlUserError[];
  };
}

export interface CustomerLocationSetActiveResult {
  customerLocationSetActive: {
    location: GqlLocationSetActiveNode | null;
    userErrors: GqlUserError[];
  };
}

/** vendorBankAccountSetActive echo (legacy BankAccountResponse shape). */
export interface VendorBankAccountSetActiveResult {
  vendorBankAccountSetActive: {
    bankAccount: GqlPartyBankAccountNode | null;
    userErrors: GqlUserError[];
  };
}

/** CurrencyOptions (the D-REF-2 BE half). */
export interface GqlCurrencyNode {
  code: string;
  nameEn: string;
  nameZh: string;
  symbol: string;
}

export interface CurrencyOptionsResult {
  currencies: GqlCurrencyNode[];
}

// ---------------------------------------------------------------------------
// Variables
// ---------------------------------------------------------------------------

/** Connection reads: the facade passes only the contract-pinned `$first`/`$after`. */
export interface MastersListVariables {
  first: number;
  after?: string | null;
  [key: string]: unknown;
}

export interface MastersDetailVariables {
  id: string;
  [key: string]: unknown;
}

/** CategoryDetailPage additionally pins `$membersFirst`. */
export interface CategoryDetailVariables extends MastersDetailVariables {
  membersFirst: number;
}
